import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  useTheme,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  TablePagination,
  Card,
  CardContent,
  alpha,
} from '@mui/material';
import { useFormik } from 'formik';
import {
  Quiz,
  Assignment,
  Add,
  Edit,
  Delete,
  Analytics,
  Upload,
  Settings,
  Category,
  History,
} from '@mui/icons-material';
import { useAuth } from '@/hooks';
import { USER_ROLES } from '@/constants/app';
import {
  useAssessments,
  useQuestions,
  useUserAttempts,
  useAssessmentUtils,
  // useAssessmentTaking,
} from '@/hooks/useAssessment';
import type { CreateAssessmentRequest } from '@/types/assessment';
import { 
  assessmentValidationSchema, 
  questionValidationSchema, 
  initialAssessmentValues, 
  initialQuestionValues 
} from '@/schemas/assessmentSchemas';
import type { DifficultyLevel, AnswerOption } from '@/constants/assessment';
import {
  TabPanel,
  AssessmentCard,
  AssessmentFilters,
  AssessmentDialog,
  QuestionDialog,
  ManagementTable,
  LoadingSkeleton,
  CategoryManagement,
  AttemptsTab,
  ExcelImportDialog,
} from '@/components/assessment';
import { AdvancedAnalytics } from '@/components/analytics/AdvancedAnalytics';
import { toast } from 'react-hot-toast';
import { assessmentService } from '@/services/assessment/assessmentService';


export const AssessmentPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [openAssessmentDialog, setOpenAssessmentDialog] = useState(false);
  const [openQuestionDialog, setOpenQuestionDialog] = useState(false);
  const [openExcelImportDialog, setOpenExcelImportDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');

  // Hooks
  const { assessments, loading: assessmentsLoading, pagination: assessmentsPagination, fetchAssessments, createAssessment, updateAssessment, deleteAssessment, getAssessment } = useAssessments();
  const { questions, loading: questionsLoading, pagination: questionsPagination, fetchQuestions, createQuestion, updateQuestion, deleteQuestion, getQuestion } = useQuestions();
  const { fetchAttempts } = useUserAttempts();
  const { categories, difficultyLevels, getDifficultyColor } = useAssessmentUtils();
  // const { startAssessment } = useAssessmentTaking();

  const isAdmin = user?.role === USER_ROLES.SUPER_ADMIN || user?.role === USER_ROLES.ADMINISTRATOR;

  // Formik forms
  const assessmentFormik = useFormik({
    initialValues: initialAssessmentValues,
    validationSchema: assessmentValidationSchema,
    onSubmit: async (values) => {
      try {
        const payload: CreateAssessmentRequest = {
          title: values.title,
          description: values.description,
          category: values.category,
          difficulty: values.difficulty as DifficultyLevel,
          configuration: {
            questionCount: values.configuration.questionCount,
            timeLimit: values.configuration.timeLimit,
            passCriteria: values.configuration.passCriteria,
            questionOrder: values.configuration.questionOrder as CreateAssessmentRequest['configuration']['questionOrder'],
            retakePolicy: values.configuration.retakePolicy,
          },
          questions: values.questions as string[],
          availabilityWindow: values.availabilityWindow,
        };
        
        if (selectedItem) {
          await updateAssessment(selectedItem._id, payload);
        } else {
          await createAssessment(payload);
        }
        handleCloseAssessmentDialog();
        fetchAssessments();
        assessmentFormik.resetForm();
      } catch (error) {
        // Error handled by hook
      }
    },
  });

  const questionFormik = useFormik({
    initialValues: initialQuestionValues,
    validationSchema: questionValidationSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          question: values.question,
          options: values.options,
          correctAnswer: values.correctAnswer as AnswerOption,
          category: values.category,
          difficulty: values.difficulty as DifficultyLevel,
          tags: values.tags as string[],
          explanation: values.explanation,
        };
        
        if (selectedItem) {
          await updateQuestion(selectedItem._id, payload);
        } else {
          await createQuestion(payload, selectedAssessmentId || undefined);
        }
        handleCloseQuestionDialog();
        // Refresh questions with current filters
        if (selectedAssessmentId) {
          fetchQuestions({}, selectedAssessmentId);
        } else {
          fetchQuestions();
        }
        questionFormik.resetForm();
      } catch (error) {
        console.log(error)
        // Error handled by hook
      }
    },
  });

  // Effects
  useEffect(() => {
    fetchAssessments();
    fetchAttempts();
    if (isAdmin) {
      fetchQuestions();
    }
  }, []);

  useEffect(() => {
    const params: any = {};
    if (searchTerm) params.search = searchTerm;
    if (filterCategory) params.category = filterCategory;
    if (filterDifficulty) params.difficulty = filterDifficulty;
    params.page = currentPage;
    params.limit = rowsPerPage;


    if (tabValue === 0) {
      fetchAssessments(params);
    } else if (tabValue === 2 && isAdmin) {
      // Manage Assessments tab
      fetchAssessments(params);
    } else if (tabValue === 3 && isAdmin) {
      if (selectedAssessmentId) {
        // Fetch questions for specific assessment
        fetchQuestions(params, selectedAssessmentId);
      } else {
        // Fetch all questions
        fetchQuestions(params);
      }
    }
  }, [searchTerm, filterCategory, filterDifficulty, currentPage, rowsPerPage, tabValue, selectedAssessmentId]);

  // Handlers
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setCurrentPage(1);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setCurrentPage(newPage + 1); // TablePagination uses 0-based indexing, but our API uses 1-based
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  // Assessment pagination handlers
  const handleAssessmentChangePage = (_: unknown, newPage: number) => {
    setCurrentPage(newPage + 1); // TablePagination uses 0-based indexing, but our API uses 1-based
  };

  const handleAssessmentChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, item: any) => {
    setAnchorEl(event.currentTarget);
    console.log(item)
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // setSelectedItem(null);
  };

  const handleOpenDialog = async (type: 'assessment' | 'question') => {
    
    // If editing, fetch item details
    console.log(selectedItem)
    if (selectedItem) {
      try {
        if (type === 'assessment') {
          const assessment = await getAssessment(selectedItem._id);
          if (assessment) {
            assessmentFormik.setFieldValue('title', assessment.title);
            assessmentFormik.setFieldValue('description', assessment.description);
            assessmentFormik.setFieldValue('category', assessment.category._id);
            assessmentFormik.setFieldValue('difficulty', assessment.difficulty);
            assessmentFormik.setFieldValue('configuration.questionCount', assessment.configuration.questionCount);
            assessmentFormik.setFieldValue('configuration.timeLimit', assessment.configuration.timeLimit);
            assessmentFormik.setFieldValue('configuration.passCriteria', assessment.configuration.passCriteria);
            assessmentFormik.setFieldValue('configuration.questionOrder', assessment.configuration.questionOrder);
            assessmentFormik.setFieldValue('configuration.retakePolicy.allowRetakes', assessment.configuration.retakePolicy.allowRetakes);
            assessmentFormik.setFieldValue('configuration.retakePolicy.maxAttempts', assessment.configuration.retakePolicy.maxAttempts);
            assessmentFormik.setFieldValue('configuration.retakePolicy.waitingPeriod', assessment.configuration.retakePolicy.waitingPeriod);
            assessmentFormik.setFieldValue('questions', assessment.questions || []);
            assessmentFormik.setFieldValue('availabilityWindow', assessment.availabilityWindow);
            console.log('Assessment values set:', assessmentFormik.values);
          }
        } else if (type === 'question') {
          const question = await getQuestion(selectedItem._id);
          console.log('---->',question)
          if (question?._id) {
            questionFormik.setFieldValue('question', question.question || '');
            questionFormik.setFieldValue('options.optionA', question.options?.optionA || '');
            questionFormik.setFieldValue('options.optionB', question.options?.optionB || '');
            questionFormik.setFieldValue('options.optionC', question.options?.optionC || '');
            questionFormik.setFieldValue('options.optionD', question.options?.optionD || '');
            questionFormik.setFieldValue('correctAnswer', question.correctAnswer || 'A');
            questionFormik.setFieldValue('category', typeof question.category === 'object' ? (question.category as any)._id : question.category);
            questionFormik.setFieldValue('difficulty', question.difficulty);
            questionFormik.setFieldValue('tags', question.tags || []);
            questionFormik.setFieldValue('explanation', question.explanation || '');
            console.log('Values set:', questionFormik.values);
          }
        }
      } catch (error) {
        console.error(`Failed to fetch ${type} for editing:`, error);
      }
    } else {
      // Reset forms for create mode
      if (type === 'assessment') {
        assessmentFormik.resetForm();
      } else {
        questionFormik.resetForm();
      }
    }
    
    if (type === 'assessment') {
      setOpenAssessmentDialog(true);
    } else {
      setOpenQuestionDialog(true);
    }
    handleMenuClose();
  };

  const handleCloseAssessmentDialog = () => {
    setOpenAssessmentDialog(false);
    setSelectedItem(null);
    assessmentFormik.resetForm();
  };

  const handleCloseQuestionDialog = () => {
    setOpenQuestionDialog(false);
    setSelectedItem(null);
    questionFormik.resetForm();
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;
    
    try {
      // Determine type based on current tab and selected item
      if (tabValue === 4 && selectedAssessmentId) {
        // In questions tab with assessment selected - delete question
        await deleteQuestion(selectedItem._id);
        // Refresh questions with current filters
        if (selectedAssessmentId) {
          fetchQuestions({}, selectedAssessmentId);
        } else {
          fetchQuestions();
        }
      } else {
        // In assessments tab - delete assessment
        await deleteAssessment(selectedItem._id);
        fetchAssessments();
      }
      handleMenuClose();
    } catch (error) {
      // Error handled by hook
    }
  };


  const handleExcelImport = () => {
    if (!selectedAssessmentId) {
      toast.error('Please select an assessment first');
      return;
    }
    setOpenExcelImportDialog(true);
  };

  const handleBulkImportQuestions = async (questions: any[]) => {
    if (!selectedAssessmentId) {
      throw new Error('No assessment selected');
    }

    try {
      const response = await assessmentService.bulkCreateAssessmentQuestions(selectedAssessmentId, questions);

      if (response.success) {
        toast.success(`${questions.length} questions imported successfully`);
        // Refresh questions with current filters
        if (selectedAssessmentId) {
          fetchQuestions({}, selectedAssessmentId);
        } else {
          fetchQuestions();
        }
        setOpenExcelImportDialog(false);
      } else {
        throw new Error(response.message || 'Import failed');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to import questions';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };


  return (
    <Box sx={{ 
      minHeight: '100vh',
      maxWidth: '100%', 
      mx: 'auto' 
    }}>
      {/* Hero Header Section */}
      <Box
        sx={{
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
          px: 3,
          py: 4,
          borderRadius: 2,
          mb: 3
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 3,
                boxShadow: 3,
              }}
            >
              <Quiz sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Assessment Center
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isAdmin 
                  ? 'Manage assessments, questions, and view analytics' 
                  : 'Take assessments and track your progress'
                }
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Quick Stats */}
        {/* <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                p: 2.5,
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2
                }
              }}
            >
              <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                {assessments?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Available Tests
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                p: 2.5,
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'success.main',
                  boxShadow: 2
                }
              }}
            >
              <Typography variant="h5" color="success.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                {categories?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Categories
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                p: 2.5,
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'warning.main',
                  boxShadow: 2
                }
              }}
            >
              <Typography variant="h5" color="warning.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                {questions?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Questions
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                p: 2.5,
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'info.main',
                  boxShadow: 2
                }
              }}
            >
              <Typography variant="h5" color="info.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                {difficultyLevels?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Difficulty Levels
              </Typography>
            </Box>
          </Grid>
        </Grid> */}
      </Box>

      {/* Tabs */}
      <Card
        elevation={0}
        sx={{
          position: 'relative',
          height: '100%',
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          borderRadius: '20px',
          backgroundColor: theme.palette.mode === 'light' 
            ? 'rgba(255, 255, 255, 0.95)' 
            : 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(20px)',
          overflow: 'hidden',
          mb: 3,
          '&:hover': {
            borderColor: alpha(theme.palette.primary.main, 0.3),
            backgroundColor: theme.palette.mode === 'light' 
              ? 'rgba(255, 255, 255, 1)' 
              : 'rgba(30, 41, 59, 1)',
            boxShadow: theme.palette.mode === 'light'
              ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`
              : `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`,
          },
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Box sx={{ borderRadius: '20px', overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          
          sx={{ 
            px: 2,
            pt: 2,
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '2px',
              backgroundColor: theme.palette.primary.main,
              bottom: 4
            },
            '& .MuiTab-root': {
              borderRadius: '12px 12px 0 0',
              margin: '0 2px',
              minHeight: 72,
              transition: 'all 0.2s ease-in-out',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                color: theme.palette.text.primary
              },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                fontWeight: 600
              }
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            icon={<Quiz sx={{ fontSize: 24, mb: 0.5 }} />} 
            label="Take Assessments" 
            sx={{ py: 1.5 }} 
          />
          <Tab 
            icon={<History sx={{ fontSize: 24, mb: 0.5 }} />} 
            label="My Attempts" 
            sx={{ py: 1.5 }} 
          />
        
          {isAdmin && (
            <Tab 
              icon={<Settings sx={{ fontSize: 24, mb: 0.5 }} />} 
              label="Manage Assessments" 
              sx={{ py: 1.5 }} 
            />
          )}
          {isAdmin && (
            <Tab 
              icon={<Assignment sx={{ fontSize: 24, mb: 0.5 }} />} 
              label="Manage Questions" 
              sx={{ py: 1.5 }} 
            />
          )}
          {isAdmin && (
            <Tab 
              icon={<Category sx={{ fontSize: 24, mb: 0.5 }} />} 
              label="Manage Categories" 
              sx={{ py: 1.5 }} 
            />
          )}
          {isAdmin && (
            <Tab 
              icon={<Analytics sx={{ fontSize: 24, mb: 0.5 }} />} 
              label="Advanced Analytics" 
              sx={{ py: 1.5 }} 
            />
          )}
        
        </Tabs>
        </Box>
      </Card>

      {/* Tab Content Panels */}
      <Box sx={{ mt: 3 }}>
        {/* Take Assessments Tab */}
        <TabPanel value={tabValue} index={0}>
          <AssessmentFilters
            searchTerm={searchTerm}
            filterCategory={filterCategory}
            filterDifficulty={filterDifficulty}
            categories={categories}
            difficultyLevels={difficultyLevels}
            onSearchChange={setSearchTerm}
            onCategoryChange={setFilterCategory}
            onDifficultyChange={setFilterDifficulty}
            onRefresh={() => fetchAssessments()}
          />

          {assessmentsLoading ? (
            <LoadingSkeleton rows={6} />
          ) : assessments && assessments.length > 0 ? (
            <>
              <Grid container spacing={2}>
                {assessments.map((assessment) => (
                  <Grid size={{ xs: 12, md: 12, lg: 6 }} key={assessment._id}>
                    <AssessmentCard
                      assessment={assessment}
                      getDifficultyColor={getDifficultyColor}
                    />
                  </Grid>
                ))}
              </Grid>
              
              {/* Table Pagination for Take Assessments */}
              <TablePagination
                component="div"
                count={assessmentsPagination.totalItems}
                page={assessmentsPagination.currentPage - 1} // Convert to 0-based indexing for TablePagination
                onPageChange={handleAssessmentChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleAssessmentChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 15, 25, 50]}
                labelRowsPerPage="Assessments per page:"
                showFirstButton
                showLastButton
                sx={{ mt: 3, borderTop: '1px solid', borderTopColor: 'divider' }}
              />
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No assessments available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isAdmin ? 'Create your first assessment to get started.' : 'Check back later for new assessments.'}
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* My Attempts Tab */}
        <TabPanel value={tabValue} index={1}>
          <AttemptsTab />
        </TabPanel>

      

        {/* Admin: Manage Assessments Tab */}
        {isAdmin && (
          <TabPanel value={tabValue} index={2}>
            <Card
              elevation={0}
              sx={{
                mb: 3,
                position: 'relative',
                height: '100%',
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                borderRadius: '20px',
                backgroundColor: theme.palette.mode === 'light' 
                  ? 'rgba(255, 255, 255, 0.95)' 
                  : 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  backgroundColor: theme.palette.mode === 'light' 
                    ? 'rgba(255, 255, 255, 1)' 
                    : 'rgba(30, 41, 59, 1)',
                  boxShadow: theme.palette.mode === 'light'
                    ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`
                    : `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`,
                },
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Assessment Management
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog('assessment')}
                  sx={{ textTransform: 'none' }}
                >
                  Create Assessment
                </Button>
              </Stack>
            </Box>

            {assessmentsLoading ? (
              <LoadingSkeleton />
            ) : assessments && assessments.length > 0 ? (
              <>
                <ManagementTable
                  data={assessments}
                  type="assessments"
                  onMenuClick={handleMenuClick}
                  getDifficultyColor={getDifficultyColor}
                />
                
                {/* Table Pagination */}
                <TablePagination
                  component="div"
                  count={assessmentsPagination.totalItems}
                  page={assessmentsPagination.currentPage - 1} // Convert to 0-based indexing for TablePagination
                  onPageChange={handleAssessmentChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleAssessmentChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 15, 25, 50]}
                  labelRowsPerPage="Assessments per page:"
                  showFirstButton
                  showLastButton
                />
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  No assessments available
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Create your first assessment to get started.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog('assessment')}
                >
                  Create First Assessment
                </Button>
              </Box>
            )}
              </CardContent>
            </Card>
          </TabPanel>
        )}

        {/* Admin: Manage Questions Tab */}
        {isAdmin && (
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Question Management
            </Typography>

            {/* Assessment Selection Card */}
            <Card 
              elevation={0}
              sx={{ 
                mb: 3, 
                position: 'relative',
                height: '100%',
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                borderRadius: '20px',
                backgroundColor: theme.palette.mode === 'light' 
                  ? 'rgba(255, 255, 255, 0.95)' 
                  : 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  backgroundColor: theme.palette.mode === 'light' 
                    ? 'rgba(255, 255, 255, 1)' 
                    : 'rgba(30, 41, 59, 1)',
                  boxShadow: theme.palette.mode === 'light'
                    ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`
                    : `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`,
                },
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Select Assessment to Manage Questions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Choose an assessment to add, edit, or import questions for that specific assessment.
                </Typography>
                
                <FormControl fullWidth sx={{ maxWidth: 400 }}>
                  <InputLabel>Select Assessment</InputLabel>
                  <Select
                    value={selectedAssessmentId}
                    label="Select Assessment"
                    onChange={(e) => setSelectedAssessmentId(e.target.value)}
                  >
                    <SelectMenuItem value="">
                      <em>Choose an assessment...</em>
                    </SelectMenuItem>
                    {assessments?.map((assessment) => (
                      <SelectMenuItem key={assessment._id} value={assessment._id}>
                        {assessment.title}
                      </SelectMenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>

            {/* Questions Management Section - Only show when assessment is selected */}
            {selectedAssessmentId ? (
              <>
                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Questions for {assessments?.find(a => a._id === selectedAssessmentId)?.title}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    {/* <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={downloadTemplate}
                      sx={{ textTransform: 'none' }}
                    >
                      Export Template
                    </Button> */}
                    <Button
                      variant="outlined"
                      startIcon={<Upload />}
                      onClick={handleExcelImport}
                      sx={{ textTransform: 'none' }}
                    >
                      Import Excel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => handleOpenDialog('question')}
                      sx={{ textTransform: 'none' }}
                    >
                      Add Question
                    </Button>
                  </Stack>
                </Box>

                {/* Questions Table */}
                {questionsLoading ? (
                  <LoadingSkeleton />
                ) : questions && questions.length > 0 ? (
                  <>
                    <ManagementTable
                      data={questions}
                      type="questions"
                      onMenuClick={handleMenuClick}
                      getDifficultyColor={getDifficultyColor}
                    />
                    
                    {/* Table Pagination */}
                    <TablePagination
                      component="div"
                      count={questionsPagination.totalItems}
                      page={questionsPagination.currentPage - 1} // Convert to 0-based indexing for TablePagination
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[5, 10, 15, 25, 50]}
                      labelRowsPerPage="Questions per page:"
                      showFirstButton
                      showLastButton
                    />
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                      No questions found for this assessment
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Start by adding your first question or importing questions from Excel.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog('question')}
                      >
                        Add First Question
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Upload />}
                        onClick={handleExcelImport}
                      >
                        Import from Excel
                      </Button>
                    </Stack>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  Select an assessment to manage questions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose an assessment from the dropdown above to view and manage its questions.
                </Typography>
              </Box>
            )}
          </TabPanel>
        )}

        {/* Admin: Manage Categories Tab */}
        {isAdmin && (
          <TabPanel value={tabValue} index={4}>
            <CategoryManagement />
          </TabPanel>
        )}

        {/* Admin: Advanced Analytics Tab */}
        {isAdmin && (
          <TabPanel value={tabValue} index={5}>
            <AdvancedAnalytics
              categories={categories}
              assessments={assessments}
              difficultyLevels={difficultyLevels}
            />
          </TabPanel>
        )}

      
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (tabValue === 4) {
            handleOpenDialog('question');
          } else {
            handleOpenDialog('assessment');
          }
        }}>
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteItem}>
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Analytics sx={{ mr: 1 }} />
          View Analytics
        </MenuItem>
      </Menu>

      <AssessmentDialog
        open={openAssessmentDialog}
        selectedItem={selectedItem}
        categories={categories}
        difficultyLevels={difficultyLevels}
        assessmentFormik={assessmentFormik}
        onClose={handleCloseAssessmentDialog}
      />

      <QuestionDialog
        open={openQuestionDialog}
        selectedItem={selectedItem}
        categories={categories}
        difficultyLevels={difficultyLevels}
        questionFormik={questionFormik}
        onClose={handleCloseQuestionDialog}
      />

      <ExcelImportDialog
        open={openExcelImportDialog}
        onClose={() => setOpenExcelImportDialog(false)}
        onImport={handleBulkImportQuestions}
        assessmentId={selectedAssessmentId}
        categories={categories}
      />
    </Box>
  );
};